import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { auth, signInWithEmailAndPassword, createUserWithEmailAndPassword, onAuthStateChanged, sendEmailVerification, sendPasswordResetEmail, User, defaultActionCodeSettings, signInWithCustomToken } from '../services/firebaseConfig';
import { loginWithBackendAPI } from '../services/authService';
import { signOut as firebaseSignOut, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { doc, setDoc, serverTimestamp, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import toast from 'react-hot-toast';
import { sanitizeString, sanitizeError, sanitizeEmail, ComponentRateLimiter, isCancelledAuthError, isPopupBlockedError } from '../utils';
import { validateEmail, validatePassword } from '../utils/validation';
import { secureLog } from '../lib/errors/logger';

function validateUserData(userData: any): { valid: boolean; message?: string } {
  if (!userData || typeof userData !== 'object') {
    return { valid: false, message: 'User data is required' };
  }
  
  if (!userData.firstName || typeof userData.firstName !== 'string' || userData.firstName.length < 2 || userData.firstName.length > 50) {
    return { valid: false, message: 'First name must be 2-50 characters long' };
  }
  
  if (!userData.lastName || typeof userData.lastName !== 'string' || userData.lastName.length < 2 || userData.lastName.length > 50) {
    return { valid: false, message: 'Last name must be 2-50 characters long' };
  }
  
  if (!userData.displayName || typeof userData.displayName !== 'string' || userData.displayName.length < 2 || userData.displayName.length > 100) {
    return { valid: false, message: 'Display name must be 2-100 characters long' };
  }
  
  if (userData.phoneNumber && (typeof userData.phoneNumber !== 'string' || userData.phoneNumber.length > 20)) {
    return { valid: false, message: 'Phone number must be less than 20 characters' };
  }
  
  return { valid: true };
}

// sanitizeError now imported from shared utils

// Use shared rate limiter
const authRateLimiter = new ComponentRateLimiter();


interface UserData {
  firstName: string;
  lastName: string;
  displayName: string;
  phoneNumber: string;
  dob?: string;
  gender?: string;
  bio?: string;
  location?: string;
  language?: string;
  timezone?: string;
  termsAccepted: boolean;
  termsAcceptedAt: Date;
  deviceFingerprint?: string;
  phoneVerified?: boolean;
}

interface ExtendedUser extends User {
  firstName?: string;
  lastName?: string;
  role?: 'buyer' | 'seller' | 'admin';
}

interface AuthContextType {
  currentUser: ExtendedUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string, userData: UserData) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  signOut: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  isEmailVerified: boolean;
  refreshAuthState: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<ExtendedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const lastVerifiedCheckRef = useRef<string | null>(null); // Track last UID we checked
  const lastVerifiedStatusRef = useRef<boolean>(false); // Track last known verification status
  // SECURITY: Track profile creation attempts to prevent duplicate operations
  const profileCreationInProgress = useRef<Set<string>>(new Set());
  
  // Removed auth domain logging - not needed in production

  // Removed redirect handler - using popup only now

  useEffect(() => {
    // Removed verbose logging - auth state listener setup happens too frequently
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Set user state immediately to prevent UI blocking
      setCurrentUser(user);
      
      // Set loading to false immediately for responsive UI
      setLoading(false);
      
      // Handle profile creation and email verification check asynchronously without blocking UI
      if (user) {
        // Defer profile operations to next tick to avoid blocking UI
        // Use requestIdleCallback if available, otherwise setTimeout with 0ms
        const deferProfileOps = typeof requestIdleCallback !== 'undefined' 
          ? requestIdleCallback 
          : (callback: () => void) => setTimeout(callback, 0);
        
        deferProfileOps(async () => {
          try {
            // Check if user profile exists, create if not
            const userDocRef = doc(db, 'Users', user.uid);
            const userDoc = await getDoc(userDocRef);
            
            // BEST PRACTICE: Firebase Auth is the source of truth for email verification
            // Update verification status if:
            // 1. New user session (different UID)
            // 2. Verification status changed (user verified in another tab/window)
            const firebaseAuthVerified = user.emailVerified || false;
            
            const isNewUserSession = lastVerifiedCheckRef.current !== user.uid;
            const statusChanged = lastVerifiedStatusRef.current !== firebaseAuthVerified;
            
            // Update if new session OR status changed (handles cross-tab verification)
            if (isNewUserSession || statusChanged) {
              lastVerifiedCheckRef.current = user.uid;
              lastVerifiedStatusRef.current = firebaseAuthVerified;
            setIsEmailVerified(firebaseAuthVerified);
            }
            
            // Only sync Firestore if profile exists AND there's a mismatch
            // Don't sync on every page load - only when needed
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const firestoreEmailVerified = userData.emailVerified === true;
              
              // Only sync if there's a mismatch (Firebase Auth is authoritative)
              // This should rarely happen - only sync when actually needed
              if (firebaseAuthVerified !== firestoreEmailVerified && firebaseAuthVerified) {
                // Fire and forget - sync Firestore to match Firebase Auth
                // Only sync when email is verified (not when unverified)
                setDoc(userDocRef, {
                  emailVerified: firebaseAuthVerified,
                  updatedAt: serverTimestamp(),
                }, { merge: true }).catch((syncError: any) => {
                  // SECURITY: Removed console.warn to prevent information leakage
                });
              }
            }
            
            // Profile exists - check if it needs updates (only for Google OAuth)
            if (userDoc.exists()) {
              const existingData = userDoc.data();
              const updates: any = {};
              
              // Only update if fields are missing and we have data from Firebase Auth
              if (!existingData.photoURL && user.photoURL) {
                updates.photoURL = user.photoURL;
              }
              if (!existingData.displayName && user.displayName) {
                updates.displayName = user.displayName;
              }
              if (!existingData.email && user.email) {
                updates.email = sanitizeEmail(user.email || '') || user.email;
              }
              
              if (Object.keys(updates).length > 0) {
                updates.updatedAt = serverTimestamp();
                // Fire and forget - don't wait for update
                setDoc(userDocRef, updates, { merge: true }).catch((updateError: any) => {
                  // Log update errors but don't block auth flow
                  try {
                    secureLog('warn', 'Error updating user profile in auth state listener', {
                      error: updateError instanceof Error ? updateError.message : 'Unknown update error',
                      errorCode: updateError?.code || 'unknown'
                    });
                  } catch (logError) {
                    // Ignore logging errors
                  }
                });
              }
            } else {
              // Profile doesn't exist - create it (only for Google OAuth users)
              const sanitizedEmail = sanitizeEmail(user.email || '') || user.email || '';
              const emailUsername = sanitizedEmail.split('@')[0] || 'User';
              
              const userData = {
                uid: user.uid,
                firstName: sanitizeString(user.displayName?.split(' ')[0] || '', 50) || 'User',
                lastName: sanitizeString(user.displayName?.split(' ').slice(1).join(' ') || '', 50) || null,
                email: sanitizedEmail,
                displayName: sanitizeString(user.displayName || emailUsername, 100) || 'User',
                phoneNumber: sanitizeString(user.phoneNumber || '', 20) || null,
                username: '',
                profilePicture: sanitizeString(user.photoURL || '', 500) || null,
                role: 'buyer',
                fcmToken: null,
                lastNotificationAt: null,
                unreadNotificationCount: 0,
                referrals: [],
                referredBy: null,
                shopName: null,
                loyaltyCoins: 0,
                tier: 'Bronze',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              };
              
              // Fire and forget - don't wait for profile creation
              setDoc(userDocRef, userData, { merge: true }).catch((createError: any) => {
                // Log profile creation errors but don't block auth flow
                try {
                  secureLog('warn', 'Profile creation error in auth state listener', {
                    error: createError instanceof Error ? createError.message : 'Unknown error',
                    errorCode: createError?.code || 'unknown'
                  });
                } catch (logError) {
                  // Ignore logging errors
                }
              });
            }
          } catch (error: any) {
            // Non-critical error - don't block auth flow
            // Log error for debugging
            try {
              secureLog('warn', 'Error in auth state listener profile operations', {
                error: error instanceof Error ? error.message : 'Unknown error',
                errorCode: error?.code || 'unknown'
              });
            } catch (logError) {
              // Ignore logging errors
            }
          }
        });
      }
    }, (error) => {
      // Handle auth state change errors silently to prevent blocking the app
      // SECURITY: Removed console.error to prevent information leakage
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    try {
      // Security: Input validation
      const emailValidation = validateEmail(email);
      if (!emailValidation.valid) {
        throw new Error(emailValidation.message || 'Invalid email address');
      }

      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.message || 'Invalid password');
      }

      // Security: Rate limiting
      const rateLimitKey = `login_${email}`;
      const rateLimitResult = authRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many login attempts. Please try again later.');
      }

      // Security: Sanitize input
      const sanitizedEmail = sanitizeEmail(email);
      if (!sanitizedEmail) {
        throw new Error('Invalid email address');
      }
      
      // Try Firebase Auth login first (for users registered via frontend)
      try {
        const userCredential = await signInWithEmailAndPassword(auth, sanitizedEmail, password);
        const user = userCredential.user;
        
        // Security: Clear rate limiting attempts on successful login
        if (user.email) {
          authRateLimiter.clear('login', user.email);
        }
        
        // Email verification check - keep user logged in but redirect to verification page
        // Throw a specific error so LoginPage can handle it appropriately
        if (!user.emailVerified) {
          // Update verification status
          setIsEmailVerified(false);
          // Throw error with specific code so LoginPage can redirect to verification page
          // User stays logged in (not signed out) so they can access verification page
          const error = new Error('Please verify your email address before signing in. Check your inbox for a verification email.');
          (error as any).code = 'auth/email-not-verified';
          throw error;
        }
        
        // User is verified - update status
        setIsEmailVerified(true);
      } catch (firebaseError: any) {
        // Extract error code from Firebase error (can be in code property or message)
        const errorCode = firebaseError?.code || 
                         (firebaseError?.message?.match(/auth\/[\w-]+/)?.[0]) ||
                         (firebaseError instanceof FirebaseError ? firebaseError.code : null);
        
        // If Firebase Auth login fails with invalid-credential, try backend API login
        // This handles users registered via backend API (with hashed passwords in Firestore)
        const isInvalidCredential = errorCode === 'auth/invalid-credential' || 
                                   errorCode === 'auth/user-not-found' || 
                                   errorCode === 'auth/wrong-password' ||
                                   (firebaseError?.message && firebaseError.message.includes('invalid-credential'));
        
        if (isInvalidCredential) {
          try {
            // Try backend API login (for users with hashed passwords in Firestore)
            const backendResponse = await loginWithBackendAPI(sanitizedEmail, password);
            
            if (backendResponse.success && backendResponse.customToken) {
              try {
              // Sign in with custom token from backend
              const userCredential = await signInWithCustomToken(auth, backendResponse.customToken);
              const user = userCredential.user;
              
              // Security: Clear rate limiting attempts on successful login
              if (user.email) {
                authRateLimiter.clear('login', user.email);
              }
              
              // BEST PRACTICE: Firebase Auth is the source of truth
              // Firebase Auth provides the latest emailVerified status after sign-in
              // Throw error if not verified so LoginPage can redirect appropriately
              if (!user.emailVerified) {
                // Update verification status
                setIsEmailVerified(false);
                // Throw error with specific code so LoginPage can redirect to verification page
                // User stays logged in (not signed out) so they can access verification page
                const error = new Error('Please verify your email address before signing in. Check your inbox for a verification email.');
                (error as any).code = 'auth/email-not-verified';
                throw error;
              }
              
              // User is verified - update status
              setIsEmailVerified(true);
              
              // Sync Firestore to match Firebase Auth (non-blocking)
              // Firestore READ rule: allow read: if userId == uid() || resource.data.role == 'seller' || isAdmin()
              // Firestore UPDATE rule: allow update: if authed() && userId == uid() && (allowed fields)
              // emailVerified and updatedAt are not in restricted fields list, so update is allowed
              try {
                const userDocRef = doc(db, 'Users', user.uid);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  const firestoreEmailVerified = userData.emailVerified === true;
                  
                  // Only sync if Firestore doesn't match Firebase Auth
                  if (!firestoreEmailVerified) {
                    // Fire and forget - sync Firestore with Firebase Auth
                    // UPDATE rule compliance: Only updating emailVerified and updatedAt (allowed fields)
                    setDoc(userDocRef, {
                      emailVerified: true,
                      updatedAt: serverTimestamp(),
                    }, { merge: true }).catch((syncError: any) => {
                      // SECURITY: Removed console.warn to prevent information leakage
                    });
                  }
                }
              } catch (firestoreError: any) {
                // SECURITY: Removed console.warn to prevent information leakage
                // Continue anyway - Firebase Auth verification is the primary check
              }
              
              return;
              } catch (tokenError: any) {
                // SECURITY: Removed console.error to prevent information leakage
                throw new Error('Login successful but authentication failed. Please try again.');
              }
            } else {
              throw new Error(backendResponse.message || 'Login failed');
            }
          } catch (backendError: any) {
            // Check if backend error is email not verified - re-throw with proper code
            const backendErrorCode = backendError?.code || '';
            if (backendErrorCode === 'EMAIL_NOT_VERIFIED') {
              // Re-throw with auth code so LoginPage can handle it
              const error = new Error(backendError.message || 'Please verify your email address before signing in.');
              (error as any).code = 'auth/email-not-verified';
              throw error;
            }
            
            // Check if backend error indicates user was created via frontend
            if (backendError?.message && backendError.message.includes('standard login method')) {
              // User was created via frontend, so Firebase Auth error is the real issue
              // Provide helpful error message based on the original Firebase error
              if (errorCode === 'auth/user-not-found') {
                throw new Error('No account found with this email address. If you just registered, please verify your email first.');
              } else if (errorCode === 'auth/wrong-password') {
                throw new Error('The password you entered is incorrect. Please try again or reset your password.');
              } else {
                throw new Error('Invalid email or password. If you just registered, please verify your email first. Otherwise, please check your credentials and try again.');
              }
            }
            
            // If backend provides a specific error message, use it
            if (backendError?.message && 
                !backendError.message.includes('Failed to login') && 
                !backendError.message.includes('Validation failed') &&
                !backendError.message.includes('Invalid request')) {
              // Check if backend error message is informative
              const backendMsg = backendError.message.toLowerCase();
              if (backendMsg.includes('not found') || backendMsg.includes('no account') || backendMsg.includes('user not found')) {
                throw new Error('No account found with this email address. Please check your email or create a new account.');
              } else if (backendMsg.includes('password') && (backendMsg.includes('incorrect') || backendMsg.includes('wrong'))) {
                throw new Error('The password you entered is incorrect. Please try again or reset your password.');
              } else if (backendMsg.includes('invalid') && backendMsg.includes('credential')) {
                throw new Error('The email or password you entered is incorrect. Please check your credentials and try again.');
              }
              throw backendError; 
            }
            
            // Re-throw the original Firebase error with a helpful message
            // Provide specific error messages to help users understand what went wrong
            if (errorCode === 'auth/user-not-found') {
              throw new Error('No account found with this email address. Please check your email or create a new account.');
            } else if (errorCode === 'auth/wrong-password') {
              throw new Error('The password you entered is incorrect. Please try again or reset your password.');
            } else if (errorCode === 'auth/invalid-credential') {
              // This could be either wrong email or wrong password - be helpful but not too specific
              throw new Error('The email or password you entered is incorrect. Please check your credentials and try again.');
            }
            throw firebaseError;
          }
        } else {
          // For other Firebase errors (network, etc.), provide user-friendly messages
          const errorMap: Record<string, string> = {
            'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
            'auth/too-many-requests': 'Too many login attempts. Please wait a few minutes and try again.',
            'auth/user-disabled': 'This account has been disabled. Please contact support for assistance.',
            'auth/operation-not-allowed': 'Email/password sign-in is disabled. Please contact support.',
            'auth/invalid-api-key': 'Authentication service error. Please contact support.',
            'auth/internal-error': 'An internal error occurred. Please try again in a moment.',
            'auth/quota-exceeded': 'Service quota exceeded. Please try again later.',
          };
          
          if (errorMap[errorCode]) {
            throw new Error(errorMap[errorCode]);
          }
          
          // For other Firebase errors, provide a helpful message if available
          if (firebaseError?.message && firebaseError.message.length > 20 && !firebaseError.message.includes('auth/')) {
            throw new Error(firebaseError.message);
          }
          
          // Fallback to generic error
          throw new Error('Login failed. Please try again or contact support if the problem persists.');
        }
      }
      
      } catch (error: any) {
        // SECURITY: Removed console.error to prevent information leakage
      
      // If error is already a user-friendly Error object, throw it as-is
      if (error instanceof Error && error.message && !error.message.includes('Operation failed') && error.message.length > 10) {
        throw error;
      }
      
      // Security: Sanitize error messages
      const sanitizedError = new Error(sanitizeError(error));
      throw sanitizedError;
    }
  };

  const loginWithGoogle = async () => {
    // Configure Google Auth Provider with proper settings for all users
    // Create provider outside try block so it's available in catch block
    const provider = new GoogleAuthProvider();
    
    try {
      // Security: Rate limiting for Google sign-in
      const rateLimitKey = 'google_signin';
      const rateLimitResult = authRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many sign-in attempts. Please try again later.');
      }

      // Configure provider settings
      
      // Add OAuth scopes for user profile information
      provider.addScope('profile');
      provider.addScope('email');
      
      // Set custom parameters to ensure OAuth works for all users
      // prompt: 'select_account' allows users to choose their account
      // This is important for multi-account scenarios and external users
      // 
      // NOTE: The application name shown in the OAuth consent screen ("to continue to baakas-197b9.firebaseapp.com")
      // is controlled by the OAuth consent screen settings in Google Cloud Console:
      // 1. Go to Google Cloud Console > APIs & Services > OAuth consent screen
      // 2. Edit the "Application name" field (e.g., change to "Baakas")
      // 3. Save changes
      // This cannot be changed through code - it must be configured in Google Cloud Console
      provider.setCustomParameters({
        prompt: 'select_account',
        // Ensure the consent screen is shown for all users
        access_type: 'online',
      });
      
      // Always use popup for Google authentication (no redirect)
      try {
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        
        // Security: Clear rate limiting attempts on successful login
        if (user.email) {
          authRateLimiter.clear('login', user.email);
        }
        
        // Update verification status immediately (Google OAuth emails are verified by default)
        // But check actual status to be safe
        const isVerified = user.emailVerified || false;
        setIsEmailVerified(isVerified);
        
        // Show success message
        toast.success('Google Sign-In successful!');
          
          // Check if this is a new user and create profile if needed (non-blocking)
          // SECURITY: Prevent duplicate profile creation attempts
          if (profileCreationInProgress.current.has(user.uid)) {
            // Profile creation already in progress for this user
            return;
          }
          
          profileCreationInProgress.current.add(user.uid);
          const userDocRef = doc(db, 'Users', user.uid);
          getDoc(userDocRef)
            .then((userDoc) => {
              if (!userDoc.exists()) {
                // Security: Sanitize user data
                const sanitizedFirstName = sanitizeString(user.displayName?.split(' ')[0] || '', 50);
                const sanitizedLastName = sanitizeString(user.displayName?.split(' ').slice(1).join(' ') || '', 50);
                const sanitizedDisplayName = sanitizeString(user.displayName || user.email?.split('@')[0] || 'User', 100);
                const sanitizedEmail = sanitizeEmail(user.email || '');
                const sanitizedPhotoURL = sanitizeString(user.photoURL || '', 500);
                const sanitizedPhoneNumber = sanitizeString(user.phoneNumber || '', 20);
                
                // Create user profile for new Google sign-in users
                // Google OAuth emails are verified by default, but use actual status
                const userData = {
                  uid: user.uid,
                  firstName: sanitizedFirstName || 'User',
                  lastName: sanitizedLastName || null,
                  email: sanitizedEmail || user.email,
                  displayName: sanitizedDisplayName || 'User',
                  phoneNumber: sanitizedPhoneNumber || null,
                  username: '',
                  profilePicture: sanitizedPhotoURL || null,
                  role: 'buyer', // Consistent with redirect flow
                  emailVerified: isVerified, // Use actual verification status
                  fcmToken: null,
                  lastNotificationAt: null,
                  unreadNotificationCount: 0,
                  referrals: [],
                  referredBy: null,
                  shopName: null,
                  loyaltyCoins: 0,
                  tier: 'Bronze',
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                };
                
                return setDoc(userDocRef, userData, { merge: true })
                  .finally(() => {
                    // Remove from in-progress set after completion
                    profileCreationInProgress.current.delete(user.uid);
                  });
              } else {
                // Update existing profile with Google photoURL if missing
                // SECURITY: Explicitly specify fields instead of spreading to prevent data injection
                const existingData = userDoc.data();
                if (!existingData.photoURL && user.photoURL) {
                  const sanitizedPhotoURL = sanitizeString(user.photoURL, 500);
                  const sanitizedDisplayName = sanitizeString(user.displayName || user.email?.split('@')[0] || 'User', 100);
                  
                  // SECURITY: Only update specific allowed fields, don't spread existingData
                  return setDoc(userDocRef, {
                    photoURL: sanitizedPhotoURL || '',
                    displayName: existingData.displayName || sanitizedDisplayName || 'User',
                    updatedAt: serverTimestamp(),
                  }, { merge: true })
                    .finally(() => {
                      // Remove from in-progress set after completion
                      profileCreationInProgress.current.delete(user.uid);
                    });
                }
                // Remove from in-progress set if no update needed
                profileCreationInProgress.current.delete(user.uid);
                return Promise.resolve();
              }
            })
            .catch((profileError: any) => {
              // Remove from in-progress set on error
              profileCreationInProgress.current.delete(user.uid);
              // Don't fail the entire login if profile creation fails
              // This is non-critical - the user is authenticated, profile can be created later
              // Log profile creation errors for debugging
              try {
                secureLog('warn', 'Error creating/updating user profile in Google popup handler', {
                  error: profileError instanceof Error ? profileError.message : 'Unknown profile error',
                  errorCode: profileError?.code || 'unknown'
                });
              } catch (logError) {
                // Ignore logging errors
              }
            });
        } catch (popupError: any) {
          // If popup is blocked, show helpful error message
          if (isPopupBlockedError(popupError)) {
            throw new Error('Popup was blocked. Please allow popups for this site and try again.');
          }
          // If user cancelled, don't show error
          if (isCancelledAuthError(popupError)) {
            throw new Error('Sign-in was cancelled');
          }
          // Rethrow other errors
          throw popupError;
        }
      
    } catch (error) {
      // Enhanced error logging to help diagnose Google sign-in issues
      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as FirebaseError;
        const errorCode = firebaseError.code;
        const errorMessage = firebaseError.message;
        
        // SECURITY: Removed console.error to prevent information leakage
        // Error details are handled via user-friendly error messages below
        
        // Don't show errors for user-cancelled actions
        if (isCancelledAuthError(firebaseError)) {
          throw new Error('Sign-in was cancelled');
        }
        
        // Check for common configuration errors
        if (errorCode === 'auth/operation-not-allowed') {
          // SECURITY: Removed console.error to prevent information leakage
          throw new Error('Google sign-in is not enabled. Please contact support.');
        }
        
        if (errorCode === 'auth/unauthorized-domain') {
          const currentDomain = window.location.hostname;
          // SECURITY: Removed console.error to prevent information leakage
          
          // Provide helpful error message based on domain type
          if (/^\d+\.\d+\.\d+\.\d+$/.test(currentDomain)) {
            throw new Error('IP addresses are not supported. Please use "localhost:3000" for local development, or add a proper domain name in Firebase Console.');
          } else {
            throw new Error(`Domain "${currentDomain}" is not authorized. Please add it in Firebase Console → Authentication → Settings → Authorized domains.`);
          }
        }
        
        // Handle popup blocked error
        if (errorCode === 'auth/popup-blocked') {
          throw new Error('Popup was blocked. Please allow popups for this site and try again.');
        }
        
        if (errorCode === 'auth/network-request-failed') {
          throw new Error('Network error. Please check your internet connection and try again.');
        }
        
        if (errorCode === 'auth/too-many-requests') {
          throw new Error('Too many sign-in attempts. Please wait a few minutes and try again.');
        }
        
        if (errorCode === 'auth/invalid-credential' || errorCode === 'auth/user-disabled') {
          throw new Error('Unable to sign in with this account. Please try a different account or contact support.');
        }
        
        // Preserve Firebase error messages - they're usually descriptive
        if (errorMessage && errorMessage.length > 20) {
          // Check if it's already a user-friendly message (contains helpful text)
          if (errorMessage.includes('localhost') || 
              errorMessage.includes('authorized') || 
              errorMessage.includes('domain') ||
              errorMessage.includes('not enabled') ||
              errorMessage.includes('contact support')) {
            throw new Error(errorMessage);
          }
          // For other Firebase errors, provide a more helpful message
          throw new Error(`Google sign-in failed: ${errorMessage}`);
        }
        
        // If we have an error code but no helpful message, provide a generic but informative message
        if (errorCode) {
          throw new Error(`Google sign-in failed (${errorCode}). Please try again or contact support.`);
        }
      } else {
        // SECURITY: Removed console.error to prevent information leakage
      }
      
      // For other errors, check if it's already a user-friendly Error object
      if (error instanceof Error) {
        // If the error message is already helpful (longer than 30 chars and contains helpful keywords), use it
        if (error.message.length > 30 && 
            (error.message.includes('localhost') || 
             error.message.includes('authorized') || 
             error.message.includes('domain') ||
             error.message.includes('not supported') ||
             error.message.includes('contact support'))) {
          throw error; // Re-throw the helpful error as-is
        }
      }
      
      // Only sanitize if we haven't handled it above
      const sanitized = sanitizeError(error);
      // If sanitization returned a generic message but original was longer, use original
      if (sanitized === 'Operation failed' && error instanceof Error && error.message.length > 30) {
        throw new Error(error.message);
      }
      throw new Error(sanitized || 'Google sign-in failed. Please try again.');
    }
  };

  const register = async (email: string, password: string, userData: UserData) => {
    // Security: Input validation
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      throw new Error(emailValidation.message || 'Invalid email address');
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      throw new Error(passwordValidation.message || 'Invalid password');
    }

    const userDataValidation = validateUserData(userData);
    if (!userDataValidation.valid) {
      throw new Error(userDataValidation.message || 'Invalid user data');
    }

    // Validate terms acceptance
    if (!userData.termsAccepted) {
      throw new Error('Terms of Service and Privacy Policy must be accepted to create an account.');
    }

    // Security: Rate limiting
    const rateLimitKey = `register_${email}`;
    const rateLimitResult = authRateLimiter.isAllowed(rateLimitKey);
    if (!rateLimitResult.allowed) {
      throw new Error(rateLimitResult.message || 'Too many registration attempts. Please try again later.');
    }

    // Security: Sanitize input
    const sanitizedEmail = sanitizeEmail(email);
    if (!sanitizedEmail) {
      throw new Error('Invalid email address');
    }

    // Password validation already done above at line 307

    let firebaseUser: User | null = null;
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, sanitizedEmail, password);
      firebaseUser = userCredential.user;
      const user = firebaseUser;

      // Security: Clear rate limiting attempts on successful registration
      if (user.email) {
        authRateLimiter.clear('register', user.email);
      }

      // Security: Verify user is authenticated before creating Firestore document
      // Firestore rule requires: authed() && userId == uid()
      if (!auth.currentUser || auth.currentUser.uid !== user.uid) {
        throw new Error('Authentication error. Please try again.');
      }

      // Create user document in Firestore immediately with emailVerified: false
      // This ensures all app features work, but access is restricted until verification
      // Firestore CREATE rule: allow create: if authed() && userId == uid()
      try {
      await setDoc(doc(db, 'Users', user.uid), {
        uid: user.uid,
        email: user.email ?? sanitizedEmail,
        firstName: sanitizeString(userData.firstName, 50) || 'User',
        lastName: sanitizeString(userData.lastName, 50) || null,
        displayName: sanitizeString(userData.displayName, 100) || 'User',
        phoneNumber: sanitizeString(userData.phoneNumber, 20) || null,
        username: '', // Empty string as per structure
        profilePicture: null, // Will be set when user uploads photo
        // Additional profile fields
        dob: sanitizeString(userData.dob, 20) || '',
        gender: sanitizeString(userData.gender, 20) || '',
        bio: sanitizeString(userData.bio, 500) || '',
        location: sanitizeString(userData.location, 100) || '',
        language: sanitizeString(userData.language, 10) || 'en',
        timezone: sanitizeString(userData.timezone, 50) || '',
        role: 'buyer', // Match backend role type
          // Email verification status - CRITICAL: Set to false initially
          emailVerified: false,
        // Terms acceptance tracking (important for legal compliance)
        termsAccepted: userData.termsAccepted || false,
        termsAcceptedAt: userData.termsAcceptedAt 
          ? Timestamp.fromDate(userData.termsAcceptedAt) 
          : serverTimestamp(),
        // Notification fields
        fcmToken: null, // Will be set when user enables notifications
        lastNotificationAt: null,
        unreadNotificationCount: 0,
        // Referral system
        referrals: [],
        referredBy: null,
        // Shop fields (for sellers)
        shopName: null,
        // Loyalty system
        loyaltyCoins: 0,
        tier: 'Bronze',
        // Security: Initialize fields required by DELETE rule
        // DELETE rule requires: activeOrdersCount == 0 && kycStatus != 'verified'
        activeOrdersCount: 0, // Required for account deletion compliance
        kycStatus: null, // Required for account deletion compliance (null != 'verified')
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      } catch (firestoreError: any) {
        // SECURITY: Removed console.error to prevent information leakage
        
        // Cleanup: Delete Firebase Auth user if Firestore creation fails
        try {
          await auth.currentUser?.delete();
        } catch (deleteError) {
          // SECURITY: Removed console.error to prevent information leakage
        }
        
        // Provide user-friendly error message
        const errorCode = firestoreError?.code || '';
        if (errorCode === 'permission-denied') {
          throw new Error('Permission denied. Please contact support if this issue persists.');
        } else if (errorCode === 'unavailable' || firestoreError?.message?.includes('network')) {
          throw new Error('Network error. Please check your connection and try again.');
        } else {
          throw new Error('Failed to create user profile. Please try again.');
        }
      }

      // Validate action code settings before sending
      if (!defaultActionCodeSettings.url) {
        toast.error('Email verification URL is not configured. Please contact support.');
      } else {
        // Validate URL format
        try {
          new URL(defaultActionCodeSettings.url);
        } catch {
          toast.error('Invalid email verification URL configuration. Please contact support.');
        }
      }
      
      // Send email verification asynchronously (non-blocking) for faster registration
      // Works on all platforms including mobile devices - Firebase Auth handles cross-platform email sending
      // Fire and forget - don't wait for email to send (improves registration speed)
      sendEmailVerification(user, defaultActionCodeSettings)
        .then(() => {
          // Email verification sent successfully - show success message
          toast.success('Verification email sent! Please check your inbox.', {
            duration: 4000,
          });
        })
        .catch((emailError: any) => {
          const emailFe = emailError as FirebaseError;
          const errorCode = emailFe.code || '';
          const errorMessage = emailFe.message || 'Unknown error';
          
          // Log error but don't fail registration - user can request verification email later
          // This is especially important on mobile where network conditions may vary
          let userFriendlyMessage = 'Account created but verification email failed to send. Please use the resend option on the verification page.';
          
          if (errorCode === 'auth/too-many-requests') {
            userFriendlyMessage = 'Account created. Too many verification emails sent. Please wait a few minutes before requesting another.';
          } else if (errorCode === 'auth/invalid-email') {
            userFriendlyMessage = 'Account created but email address is invalid. Please contact support.';
          } else if (errorCode === 'auth/operation-not-allowed') {
            userFriendlyMessage = 'Email verification is disabled. Please contact support.';
          } else if (errorCode === 'auth/network-request-failed') {
            // Mobile-specific: Network errors are more common on mobile
            userFriendlyMessage = 'Account created but network error prevented email from sending. Please check your connection and use the resend option.';
          } else if (errorMessage.includes('authorized domain') || errorMessage.includes('unauthorized domain')) {
            userFriendlyMessage = 'Account created but email verification failed. Please contact support - domain configuration issue.';
          }
          
          toast.error(userFriendlyMessage);
        });

      // Update verification status (user is not verified yet)
      setIsEmailVerified(false);

      // Keep user logged in - they will be restricted to verification page until verified
      // Show success message with email verification info
      // Note: This toast will show before navigation to email-verification page
      toast.success("Registration successful! Please check your email to verify your account.", {
        icon: '📧',
        duration: 5000,
      });
    } catch (err: any) {
      // Cleanup: If Firebase Auth user was created but registration failed, delete it
      if (firebaseUser) {
        try {
          await auth.currentUser?.delete();
        } catch (cleanupError) {
          // Log cleanup errors but don't expose to user
          try {
            secureLog('warn', 'Error cleaning up Firebase Auth user after registration failure', {
              error: cleanupError instanceof Error ? cleanupError.message : 'Unknown cleanup error',
              errorCode: (cleanupError as any)?.code || 'unknown'
            });
          } catch (logError) {
            // Ignore logging errors
          }
        }
      }
      
      const fe = err as FirebaseError;
      // SECURITY: Removed console.error to prevent information leakage
      
      // User-friendly error messages with specific details
      const msgMap: Record<string, string> = {
        "auth/invalid-api-key": "Authentication service error. Please contact support.",
        "auth/email-already-in-use": "This email is already registered. Please sign in with this email or use a different email address.",
        "auth/invalid-email": "Please enter a valid email address. Check for typos and try again.",
        "auth/weak-password": "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.",
        "auth/network-request-failed": "Network error. Please check your internet connection and try again.",
        "auth/operation-not-allowed": "Email/password registration is disabled. Please contact support.",
        "auth/too-many-requests": "Too many registration attempts. Please wait a few minutes and try again.",
        "auth/user-disabled": "This account has been disabled. Please contact support for assistance.",
      };
      
      // Check if error message already contains user-friendly text
      let errorMessage = msgMap[fe.code] || err.message || "Registration failed. Please try again.";
      
      // If it's a custom error we threw, use that message
      if (err.message && !err.message.includes('auth/')) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
      
      // Throw error with user-friendly message
      throw new Error(errorMessage);
    }
  };

  const resetPassword = async (email: string) => {
    // Security: Sanitize input
    const sanitized = sanitizeEmail(email);
    if (!sanitized) {
      throw new Error('Invalid email format. Please enter a valid email address.');
    }

    try {
      // Password reset is purely Firebase Auth operation - no Firestore interaction required
      // Firestore rules do not apply to password reset as it doesn't modify Firestore documents
      
      // Validate action code settings before sending
      if (!defaultActionCodeSettings.url) {
        throw new Error('Password reset URL is not configured. Please contact support.');
      }
      
      // Ensure URL is valid
      let actionCodeUrl: URL;
      try {
        actionCodeUrl = new URL(defaultActionCodeSettings.url);
      } catch {
        throw new Error('Invalid password reset URL configuration. Please contact support.');
      }
      
      // Log the URL being used for debugging (only in development)
      if (process.env.NODE_ENV === 'development') {
        secureLog('info', 'Sending password reset email', {
          email: sanitized,
          resetUrl: defaultActionCodeSettings.url,
          handleCodeInApp: defaultActionCodeSettings.handleCodeInApp
        });
      }
      
      await sendPasswordResetEmail(auth, sanitized, defaultActionCodeSettings);
      // Success - email should be sent (no error thrown means success)
      
      // Log success in development
      if (process.env.NODE_ENV === 'development') {
        secureLog('info', 'Password reset email sent successfully', { 
          email: sanitized,
          resetUrl: defaultActionCodeSettings.url 
        });
      }
    } catch (err: any) {
      // Get error code from multiple possible locations
      const errorCode = (err as FirebaseError)?.code || err?.code || '';
      const errorMessage = err?.message || '';
      
      // Enhanced error logging for debugging
      try {
        const authConfig = auth?.app?.options || {};
        secureLog('error', 'Password reset email failed', {
          errorCode,
          errorMessage: errorMessage.substring(0, 200),
          email: sanitized,
          resetUrl: defaultActionCodeSettings.url,
          authDomain: authConfig.authDomain || 'unknown',
          projectId: authConfig.projectId || 'unknown'
        });
      } catch (logError) {
        // Fallback logging if config access fails
        secureLog('error', 'Password reset email failed', {
          errorCode,
          errorMessage: errorMessage.substring(0, 200),
          email: sanitized
        });
      }
      
      // User-friendly error messages with specific details
      const msgMap: Record<string, string> = {
        "auth/invalid-api-key": "Authentication service error. Please contact support.",
        "auth/invalid-email": "Please enter a valid email address. Check for typos and try again.",
        "auth/user-not-found": "No account found with this email address. Please check your email or create a new account.",
        "auth/network-request-failed": "Network error. Please check your internet connection and try again.",
        "auth/too-many-requests": "Too many password reset requests. Please wait a few minutes and try again.",
        "auth/operation-not-allowed": "Password reset is currently disabled. Please contact support for assistance.",
        "auth/quota-exceeded": "Password reset quota exceeded. Please try again later or contact support.",
        "auth/internal-error": "An internal error occurred. Please try again in a moment.",
        "auth/unauthorized-domain": "This domain is not authorized. Please contact support.",
      };
      
      // Determine the error message to show
      let userFriendlyMessage = msgMap[errorCode] || "Failed to send password reset email. Please try again.";
      
      // Use custom error message if provided and it's not a Firebase auth error
      if (errorMessage && !errorCode.startsWith('auth/') && !msgMap[errorCode]) {
        userFriendlyMessage = errorMessage;
      }
      
      // Preserve Firebase error code if it exists for proper error handling
      const error = new Error(userFriendlyMessage);
      if (errorCode) {
        (error as any).code = errorCode;
      }
      // Don't show toast here - let withErrorHandling handle it to avoid duplicates
      throw error;
    }
  };

  const sendVerificationEmail = async () => {
    try {
      if (!currentUser) {
        throw new Error('No user logged in. Please log in to send verification email.');
      }

      if (currentUser.emailVerified) {
        // Email already verified - update state and return
        setIsEmailVerified(true);
        return;
      }
      
      // Ensure verification status is set correctly
      setIsEmailVerified(false);

        // Security: Rate limiting for email verification
        const rateLimitKey = `email_verification_${currentUser.uid}`;
        const rateLimitResult = authRateLimiter.isAllowed(rateLimitKey);
        if (!rateLimitResult.allowed) {
          throw new Error(rateLimitResult.message || 'Too many verification email requests. Please try again later.');
        }

      // Validate email before sending
      if (!currentUser.email) {
        throw new Error('No email address associated with this account. Please contact support.');
      }

      try {
        // Removed debug log to prevent sensitive data exposure (URL may contain tokens)
        
        await sendEmailVerification(currentUser as User, defaultActionCodeSettings);
        
        // Removed debug log to prevent sensitive data exposure (email)
      } catch (sendError: any) {
        const fe = sendError as FirebaseError;
        const errorCode = fe.code || '';
        const errorMessage = fe.message || 'Unknown error';
        
        // Enhanced error logging for debugging
        // SECURITY: Removed console.error to prevent information leakage
        // Error details are handled via user-friendly error messages below
      
        // User-friendly error messages with specific details
        const msgMap: Record<string, string> = {
          "auth/invalid-api-key": "Authentication service error. Please contact support.",
          "auth/invalid-email": "Invalid email address. Please check your email and try again.",
          "auth/network-request-failed": "Network error. Please check your internet connection and try again.",
          "auth/too-many-requests": "Too many verification email requests. Please wait a few minutes before requesting another.",
          "auth/user-not-found": "User account not found. Please try logging in again.",
          "auth/operation-not-allowed": "Email verification is currently disabled. Please contact support for assistance.",
        };
        
        let userFriendlyMessage = msgMap[errorCode] || "Failed to send verification email. Please try again.";
        
        // Check for domain authorization issues
        if (errorMessage.includes('authorized domain') || errorMessage.includes('unauthorized domain')) {
          userFriendlyMessage = "Email verification failed. Please contact support - domain configuration issue.";
          // SECURITY: Removed console.error to prevent information leakage
        }
        
        // Handle blocked requests
        if (errorMessage.includes('ERR_BLOCKED_BY_CLIENT') || errorMessage.includes('blocked')) {
          userFriendlyMessage = "Email verification request was blocked. Please disable ad blockers and try again.";
          // SECURITY: Removed console.warn to prevent information leakage
        }
        
        try {
          toast.error(userFriendlyMessage);
        } catch (toastError) {
          // Log toast errors but don't expose to user
          try {
            secureLog('warn', 'Error showing toast notification', {
              error: toastError instanceof Error ? toastError.message : 'Unknown toast error'
            });
          } catch (logError) {
            // Ignore logging errors
          }
        }
        
        // Throw error with user-friendly message
        throw new Error(userFriendlyMessage);
      }
    } catch (error: any) {
      // If it's already a user-friendly error, re-throw it
      if (error?.message && !error.message.includes('auth/') && error.message.length > 20) {
        throw error;
      }
      
      // Security: Sanitize error messages
      const sanitizedError = new Error(sanitizeError(error));
      throw sanitizedError;
    }
  };

  const refreshAuthState = async () => {
    try {
      if (!auth) {
        // SECURITY: Removed console.warn to prevent information leakage
        return;
      }

      if (auth.currentUser) {
        try {
        // Force a token refresh to ensure the user is still valid
        await auth.currentUser.getIdToken(true);
        
        // Update verification status after token refresh
        const isVerified = auth.currentUser.emailVerified || false;
        setIsEmailVerified(isVerified);
        } catch (tokenError: any) {
          const errorMessage = tokenError?.message || '';
          const errorCode = tokenError?.code || '';
          
          // Handle blocked requests gracefully
          if (errorMessage.includes('ERR_BLOCKED_BY_CLIENT') || errorMessage.includes('blocked')) {
            // SECURITY: Removed console.warn to prevent information leakage
            // Update verification status from cached user
            if (auth.currentUser) {
              const isVerified = auth.currentUser.emailVerified || false;
              setIsEmailVerified(isVerified);
            }
            return; // Continue with cached token
          }
          
          // Handle session expiration
          if (errorCode === 'auth/user-token-expired' || errorCode === 'auth/user-not-found') {
            // SECURITY: Removed console.warn to prevent information leakage
            setCurrentUser(null);
            setIsEmailVerified(false);
            return;
          }
          
          throw tokenError;
        }
      } else {
        // No user - clear verification status
        setIsEmailVerified(false);
      }
    } catch (error) {
      // Log refresh errors for debugging
      try {
        secureLog('error', 'Error refreshing auth state', {
          error: error instanceof Error ? error.message : 'Unknown error',
          errorCode: (error as any)?.code || 'unknown'
        });
      } catch (logError) {
        // Ignore logging errors
      }
      // If refresh fails, the user might have been logged out
      setCurrentUser(null);
      setIsEmailVerified(false);
    }
  };

  const logout = async () => {
    try {
      // Validate auth is available
      if (!auth) {
        throw new Error('Authentication service not available. Please refresh the page.');
      }

      // Reset verification check refs on logout
      lastVerifiedCheckRef.current = null;
      lastVerifiedStatusRef.current = false;
      setIsEmailVerified(false);

      // Security: Clear any rate limiting attempts on logout
      if (currentUser?.email) {
        try {
        authRateLimiter.clear('login', currentUser.email);
        authRateLimiter.clear('register', currentUser.email);
        authRateLimiter.clear('password_reset', currentUser.email);
        } catch (clearError) {
          // SECURITY: Removed console.warn to prevent information leakage
          // Continue with logout anyway
        }
      }
      
      try {
      await firebaseSignOut(auth);
      } catch (signOutError: any) {
        const errorMessage = signOutError?.message || '';
        const errorCode = signOutError?.code || '';
        
        // Handle blocked requests
        if (errorMessage.includes('ERR_BLOCKED_BY_CLIENT') || errorMessage.includes('blocked')) {
          // SECURITY: Removed console.warn to prevent information leakage
          // Clear local state even if sign out is blocked
          setCurrentUser(null);
          return;
        }
        
        // Handle network errors
        if (errorCode === 'auth/network-request-failed') {
          // Clear local state even on network error
          setCurrentUser(null);
          throw new Error('Network error during logout. You have been logged out locally.');
        }
        
        throw signOutError;
      }
    } catch (error) {
      // Security: Sanitize error messages
      const sanitizedError = new Error(sanitizeError(error));
      throw sanitizedError;
    }
  };

  const value: AuthContextType = {
    currentUser,
    loading,
    login,
    loginWithGoogle,
    register,
    resetPassword,
    logout,
    signOut: logout,
    sendVerificationEmail,
    isEmailVerified,
    refreshAuthState,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Admin role hooks
export const useUserRole = () => {
  const { currentUser } = useAuth();
  return currentUser?.role || 'buyer';
};

export const useRequireAdmin = () => {
  const { currentUser } = useAuth();
  
  return currentUser?.role === 'admin';
}; 